/**
 * DevStudio modul — top-level page
 *
 * Project picker → DevstudioWorkspace (split layout chat | iframe).
 * Detail UI v components/devstudio/.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Wand2 } from 'lucide-react';
import { Card, CardContent, EmptyState, PageHeader, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { DevstudioWorkspace } from '../../components/devstudio/DevstudioWorkspace';


interface OrchProject {
  name: string;
  slug?: string;
  status?: string;
}

interface DevstudioSession {
  id: string;
  project_id: string;
  project_slug: string | null;
  preview_url: string | null;
  last_active: string;
}

export default function DevStudioPage() {
  const { t } = useTranslation();
  const [activeProject, setActiveProject] = useState<string>('');
  const [activeSessionId, setActiveSessionId] = useState<string>('');

  // Seznam orchestrator projektů
  const projects = useQuery<OrchProject[]>({
    queryKey: ['orch-list'],
    queryFn: async () => {
      try {
        const parsed = await api.mcp<OrchProject[] | { projects?: OrchProject[] }>(
          'orchestrator.orch_list',
          {},
        );
        return Array.isArray(parsed) ? parsed : (parsed?.projects ?? []);
      } catch {
        return [];
      }
    },
    staleTime: 30_000,
  });

  // Existující sessions pro vybraný projekt
  const sessions = useQuery<DevstudioSession[]>({
    queryKey: ['devstudio-sessions', activeProject],
    queryFn: async () => {
      if (!activeProject) return [];
      const res = await api.mcp<{ sessions?: DevstudioSession[] }>(
        'devstudio.session.list',
        { project_id: activeProject },
      );
      return res.sessions ?? [];
    },
    enabled: Boolean(activeProject),
  });

  // Auto-pick poslední session nebo vytvoř novou
  useEffect(() => {
    if (!activeProject) {
      setActiveSessionId('');
      return;
    }
    if (sessions.data && sessions.data.length > 0) {
      setActiveSessionId(sessions.data[0]!.id);
    } else if (sessions.data && sessions.data.length === 0) {
      api.mcp<{ session_id: string }>('devstudio.session.create', {
        project_id: activeProject,
      }).then((res) => {
        if (res.session_id) setActiveSessionId(res.session_id);
      }).catch(() => { /* toast později */ });
    }
  }, [activeProject, sessions.data]);

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        icon={<Wand2 size={20} />}
        title="DevStudio"
        subtitle={t('devstudio.subtitle')}
        actions={
          <Select
            value={activeProject}
            onChange={(e) => setActiveProject(e.target.value)}
            className="min-w-[240px]"
          >
            <option value="">{t('devstudio.selectProjectPlaceholder')}</option>
            {projects.data?.map((p) => (
              <option key={p.name} value={p.name}>
                {p.slug ?? p.name}
              </option>
            ))}
          </Select>
        }
      />

      {!activeProject
        ? (
          <Card className="m-4">
            <CardContent>
              <EmptyState
                icon={<Wand2 size={32} />}
                title={t('devstudio.selectProjectTitle')}
                description={t('devstudio.selectProjectDesc')}
              />
            </CardContent>
          </Card>
        )
        : !activeSessionId
        ? <div className="p-4 text-sm opacity-60">{t('devstudio.loadingSession')}</div>
        : <DevstudioWorkspace sessionId={activeSessionId} projectId={activeProject} />}
    </div>
  );
}
